import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ChatTab from "@/components/ChatTab";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload, Search, FileText, Trash2, RefreshCw, Database,
  Clock, Cpu, Layers, AlertCircle, CheckCircle2,
  FileType, Loader2, X, BookOpen, Zap, BarChart3, Shield,
  TrendingUp, Activity, Filter, ChevronLeft, ChevronRight, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { staggerContainer, staggerItem } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  ready: { label: "Ready", color: "bg-emerald-500/10 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  queued: { label: "Queued", color: "bg-amber-500/10 text-amber-700 border-amber-200", icon: Clock },
  parsing: { label: "Parsing", color: "bg-blue-500/10 text-blue-700 border-blue-200", icon: Cpu },
  chunking: { label: "Chunking", color: "bg-violet-500/10 text-violet-700 border-violet-200", icon: Layers },
  embedding: { label: "Embedding", color: "bg-cyan-500/10 text-cyan-700 border-cyan-200", icon: Zap },
  failed: { label: "Failed", color: "bg-red-500/10 text-red-700 border-red-200", icon: AlertCircle },
};

const FILE_COLORS = {
  pdf: "text-red-500", docx: "text-blue-500", md: "text-gray-500",
  txt: "text-gray-400", csv: "text-green-500", html: "text-orange-500", pptx: "text-amber-500",
};

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const StatCard = ({ label, value, icon: Icon, subtitle, trend }) => (
  <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-[var(--qn-foreground-muted)] uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-[var(--qn-foreground)] mt-1 font-mono">{value}</p>
          {subtitle && <p className="text-xs text-[var(--qn-foreground-subtle)] mt-0.5">{subtitle}</p>}
        </div>
        <div className="w-10 h-10 rounded-lg bg-[var(--qn-primary-muted)] flex items-center justify-center">
          <Icon className="w-5 h-5 text-[var(--qn-primary)]" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ========== DOCUMENTS TAB ==========
function DocumentsTab({ documents, loading, onDelete, onRefresh }) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const PAGE_SIZE = 20;

  const filtered = documents.filter((d) => {
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (typeFilter !== "all" && d.file_type !== typeFilter) return false;
    return true;
  });
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  return (
    <div className="space-y-4" data-testid="documents-tab" data-automation-id="documents-tab">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-[var(--qn-foreground-muted)]" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-8 text-xs bg-[var(--qn-surface)]" data-testid="status-filter" data-automation-id="status-filter">
              <SelectValue placeholder={t("knowledge.documents.filter_all", "All Status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("knowledge.documents.filter_all", "All Status")}</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[120px] h-8 text-xs bg-[var(--qn-surface)]" data-testid="type-filter" data-automation-id="type-filter">
              <SelectValue placeholder={t("knowledge.documents.filter_type", "All Types")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("knowledge.documents.filter_type", "All Types")}</SelectItem>
              {["pdf", "docx", "md", "txt", "csv", "html", "pptx"].map((ft) => (
                <SelectItem key={ft} value={ft}>.{ft}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <span className="text-xs text-[var(--qn-foreground-subtle)]">{filtered.length} documents</span>
        <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="refresh-docs-btn" data-automation-id="refresh-docs-btn">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[var(--qn-primary)] animate-spin" /></div>
      ) : paged.length === 0 ? (
        <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]">
          <CardContent className="p-12 text-center">
            <Database className="w-10 h-10 text-[var(--qn-foreground-subtle)] mx-auto mb-3" />
            <p className="text-sm text-[var(--qn-foreground-muted)]">{t("knowledge.documents.empty", "No documents uploaded yet")}</p>
            <p className="text-xs text-[var(--qn-foreground-subtle)] mt-1">{t("knowledge.documents.empty_description", "Upload your first document to start building your knowledge base")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {paged.map((doc) => {
            const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.queued;
            const StatusIcon = sc.icon;
            return (
              <motion.div key={doc.document_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="group">
                <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)] hover:border-[var(--qn-foreground-subtle)] transition-colors">
                  <CardContent className="p-3.5">
                    <div className="flex items-center gap-3">
                      <div className="shrink-0 w-9 h-9 rounded-lg bg-[var(--qn-surface)] flex items-center justify-center">
                        <FileType className={`w-4 h-4 ${FILE_COLORS[doc.file_type] || "text-gray-400"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[var(--qn-foreground)] truncate">{doc.title || doc.filename}</p>
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${sc.color}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />{sc.label}
                          </Badge>
                          {doc.pii_detected && (
                            <Badge variant="outline" className="text-[10px] shrink-0 bg-amber-500/10 text-amber-700 border-amber-200">
                              <Shield className="w-3 h-3 mr-1" />PII
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[var(--qn-foreground-subtle)]">
                          <span>.{doc.file_type}</span>
                          <span>{formatBytes(doc.file_size_bytes)}</span>
                          {doc.chunk_count > 0 && <span>{doc.chunk_count} chunks</span>}
                          {doc.total_tokens > 0 && <span>{doc.total_tokens.toLocaleString()} tokens</span>}
                          <span>{formatTimeAgo(doc.created_at)}</span>
                        </div>
                        {doc.error_message && <p className="text-xs text-red-500 mt-1 truncate">{doc.error_message}</p>}
                        {doc.tags?.length > 0 && (
                          <div className="flex gap-1 mt-1.5">
                            {doc.tags.map((tag) => <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">{tag}</Badge>)}
                          </div>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 text-[var(--qn-foreground-muted)] hover:text-red-500"
                        onClick={() => setDeleteDialog(doc)} data-testid={`delete-doc-${doc.document_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-[var(--qn-foreground-muted)]">{page + 1} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--qn-foreground)]">{t("knowledge.documents.delete", "Delete")}</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">
              {t("knowledge.documents.delete_confirm", "Are you sure you want to delete this document?")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog(null)} data-testid="cancel-delete-btn" data-automation-id="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" onClick={() => { onDelete(deleteDialog?.document_id); setDeleteDialog(null); }} data-testid="confirm-delete-btn" data-automation-id="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== UPLOAD TAB ==========
function UploadTab({ onRefresh }) {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const results = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", file.name);
        const res = await axios.post(`${API}/knowledge/upload`, fd);
        results.push({ ...res.data, status: "queued" });
        toast.success(`${t("knowledge.upload.success", "Uploaded")}: ${file.name}`);
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || t("knowledge.upload.error", "Upload failed")}`);
      }
    }
    setRecentUploads((prev) => [...results, ...prev]);
    setUploading(false);
    onRefresh();
  };

  // Poll recent uploads for status updates
  useEffect(() => {
    if (recentUploads.length === 0) return;
    const pending = recentUploads.filter((u) => !["ready", "failed"].includes(u.status));
    if (pending.length === 0) return;
    const timer = setInterval(async () => {
      for (const u of pending) {
        try {
          const res = await axios.get(`${API}/knowledge/documents/${u.document_id}`);
          setRecentUploads((prev) =>
            prev.map((p) => p.document_id === u.document_id ? { ...p, status: res.data.status } : p)
          );
          if (res.data.status === "ready") {
            toast.success(`${u.filename} processed successfully`);
            onRefresh();
          } else if (res.data.status === "failed") {
            toast.error(`${u.filename} processing failed`);
          }
        } catch {}
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [recentUploads, onRefresh]);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); };

  return (
    <div className="space-y-4" data-testid="upload-tab" data-automation-id="upload-tab">
      <Card
        className={`border-2 border-dashed transition-colors cursor-pointer ${
          dragOver ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)]" : "border-[var(--qn-border)] bg-[var(--qn-card)] hover:border-[var(--qn-foreground-subtle)]"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById("kb-file-input")?.click()}
        data-testid="upload-zone" data-automation-id="upload-zone"
      >
        <CardContent className="p-12 flex flex-col items-center justify-center text-center">
          {uploading ? (
            <Loader2 className="w-10 h-10 text-[var(--qn-primary)] animate-spin" />
          ) : (
            <Upload className="w-10 h-10 text-[var(--qn-foreground-muted)] mb-3" />
          )}
          <p className="text-sm font-medium text-[var(--qn-foreground)] mt-2">
            {uploading ? t("knowledge.upload.uploading", "Uploading...") : t("knowledge.upload.drag_drop", "Drag and drop files here, or click to browse")}
          </p>
          <p className="text-xs text-[var(--qn-foreground-subtle)] mt-1">
            {t("knowledge.upload.supported_formats", "Supported: PDF, DOCX, Markdown, TXT, CSV, HTML, PPTX (max 50MB)")}
          </p>
          <input id="kb-file-input" type="file" multiple accept=".pdf,.docx,.md,.txt,.csv,.html,.pptx" className="hidden"
            data-testid="file-input" data-automation-id="file-input" onChange={(e) => handleUpload(e.target.files)} />
        </CardContent>
      </Card>
      {recentUploads.length > 0 && (
        <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[var(--qn-foreground)]">
              {t("knowledge.upload.processing", "Processing Status")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentUploads.map((u) => {
              const sc = STATUS_CONFIG[u.status] || STATUS_CONFIG.queued;
              const Ic = sc.icon;
              return (
                <div key={u.document_id} className="flex items-center gap-3 text-sm" data-testid={`upload-status-${u.document_id}`}>
                  <Ic className={`w-4 h-4 ${u.status === "ready" ? "text-emerald-500" : u.status === "failed" ? "text-red-500" : "text-amber-500 animate-pulse"}`} />
                  <span className="text-[var(--qn-foreground)] truncate flex-1">{u.filename}</span>
                  <Badge variant="outline" className={`text-[10px] ${sc.color}`}>{sc.label}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ========== SEARCH TAB ==========
function SearchTab() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await axios.post(`${API}/knowledge/search`, { query });
      setResults(res.data);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const relevanceColor = (score) => {
    if (score >= 0.90) return "text-emerald-600 bg-emerald-50 border-emerald-200";
    if (score >= 0.75) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  return (
    <div className="space-y-4" data-testid="search-tab" data-automation-id="search-tab">
      <div className="flex gap-2">
        <Input placeholder={t("knowledge.search.placeholder", "Search your knowledge base...")}
          value={query} onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          data-testid="search-input" data-automation-id="search-input" className="bg-[var(--qn-surface)] border-[var(--qn-border)]" />
        <Button onClick={handleSearch} disabled={searching || !query.trim()} data-testid="search-btn" data-automation-id="search-btn" className="shrink-0 px-6">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" />Search</>}
        </Button>
      </div>
      {results && (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-[var(--qn-foreground-muted)]">
            <span>{results.chunks?.length ?? 0} results &middot; strategy: {results.strategy} &middot; {Math.round(results.latency_ms)}ms</span>
            <button onClick={() => setResults(null)} className="hover:text-[var(--qn-foreground)]"><X className="w-3 h-3" /></button>
          </div>
          {results.chunks?.length === 0 && (
            <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]">
              <CardContent className="p-8 text-center">
                <Search className="w-8 h-8 text-[var(--qn-foreground-subtle)] mx-auto mb-2" />
                <p className="text-sm text-[var(--qn-foreground-muted)]">{t("knowledge.search.no_results", "No matching chunks found")}</p>
              </CardContent>
            </Card>
          )}
          {results.chunks?.map((chunk, i) => (
            <Card key={i} className="border border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid={`search-result-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-[var(--qn-foreground-muted)]" />
                    <span className="text-sm font-medium text-[var(--qn-foreground)]">{chunk.document_title}</span>
                    {chunk.section_header && (
                      <span className="text-xs text-[var(--qn-foreground-subtle)]">&middot; {chunk.section_header}</span>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-xs font-mono ${relevanceColor(chunk.relevance_score)}`}>
                    {(chunk.relevance_score * 100).toFixed(0)}%
                  </Badge>
                </div>
                <p className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed whitespace-pre-wrap">{chunk.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== ANALYTICS TAB ==========
function AnalyticsTab() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [sumRes, dailyRes] = await Promise.all([
          axios.get(`${API}/rag/summary`),
          axios.get(`${API}/rag/daily?days=30`),
        ]);
        setSummary(sumRes.data);
        setDaily(dailyRes.data.daily || []);
      } catch (err) { console.error("Failed to fetch RAG analytics:", err); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[var(--qn-primary)] animate-spin" /></div>;
  if (!summary) return <p className="text-sm text-[var(--qn-foreground-muted)] text-center py-8">Unable to load analytics</p>;

  const strategies = summary.retrieval?.strategies || {};
  const totalStrats = Object.values(strategies).reduce((a, b) => a + b, 0) || 1;
  const stratBars = [
    { key: "skip", label: "Skip", color: "bg-gray-400", pct: strategies.skip / totalStrats * 100 },
    { key: "cache_only", label: "Cache", color: "bg-blue-400", pct: strategies.cache_only / totalStrats * 100 },
    { key: "single_hop", label: "Single", color: "bg-emerald-400", pct: strategies.single_hop / totalStrats * 100 },
    { key: "multi_hop", label: "Multi", color: "bg-violet-400", pct: strategies.multi_hop / totalStrats * 100 },
    { key: "decomposed", label: "Decomposed", color: "bg-amber-400", pct: strategies.decomposed / totalStrats * 100 },
  ];

  return (
    <div className="space-y-4" data-testid="analytics-tab" data-automation-id="analytics-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t("knowledge.analytics.kb_size", "Knowledge Base")} value={summary.knowledge_base.total_documents}
          icon={BookOpen} subtitle={`${summary.knowledge_base.total_chunks} chunks, ${summary.knowledge_base.total_tokens.toLocaleString()} tokens`} />
        <StatCard label={t("knowledge.analytics.rag_savings", "RAG Net Savings")} value={`$${summary.savings.net_savings_usd.toFixed(4)}`}
          icon={TrendingUp} subtitle={`ROI: ${summary.savings.roi}`} />
        <StatCard label={t("knowledge.analytics.retrieval_performance", "Retrievals")} value={summary.retrieval.total_retrievals}
          icon={Search} subtitle={`Cache: ${summary.retrieval.cache_hit_rate}`} />
        <StatCard label={t("knowledge.analytics.avg_latency", "Avg Latency")} value={`${summary.retrieval.avg_search_latency_ms}ms`}
          icon={Activity} subtitle={`${summary.retrieval.total_chunks_injected} chunks injected`} />
      </div>

      {/* Strategy Breakdown */}
      <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-[var(--qn-foreground)]">Strategy Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-6 rounded-full overflow-hidden bg-[var(--qn-surface)]">
            {stratBars.filter(s => s.pct > 0).map((s) => (
              <div key={s.key} className={`${s.color} transition-all`} style={{ width: `${Math.max(s.pct, 2)}%` }}
                title={`${s.label}: ${s.pct.toFixed(1)}%`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-3">
            {stratBars.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5 text-xs text-[var(--qn-foreground-muted)]">
                <div className={`w-2.5 h-2.5 rounded-sm ${s.color}`} />
                {s.label}: {strategies[s.key] || 0}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Daily Activity */}
      {daily.length > 0 && (
        <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[var(--qn-foreground)]">
              {t("knowledge.analytics.daily_activity", "Daily Activity")} (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[2px] h-20">
              {daily.map((d, i) => {
                const maxR = Math.max(...daily.map(x => x.retrievals), 1);
                const h = (d.retrievals / maxR) * 100;
                return (
                  <div key={i} className="flex-1 bg-[var(--qn-primary)] rounded-t-sm opacity-70 hover:opacity-100 transition-opacity"
                    style={{ height: `${Math.max(h, 2)}%` }}
                    title={`${d.date}: ${d.retrievals} retrievals, ${d.chunks_injected} chunks`} />
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-[var(--qn-foreground-subtle)]">
              <span>{daily[0]?.date}</span>
              <span>{daily[daily.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Policy Info */}
      <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-[var(--qn-foreground)]">Retrieval Policy</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.policy?.status === "using_defaults" ? (
            <p className="text-xs text-[var(--qn-foreground-muted)]">Using default retrieval policy. Policy will be optimized automatically after 50+ retrievals.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><span className="text-[var(--qn-foreground-subtle)]">Threshold</span><p className="font-mono font-medium text-[var(--qn-foreground)]">{summary.policy?.similarity_threshold ?? "—"}</p></div>
              <div><span className="text-[var(--qn-foreground-subtle)]">Context Ratio</span><p className="font-mono font-medium text-[var(--qn-foreground)]">{summary.policy?.max_context_ratio ?? "—"}</p></div>
              <div><span className="text-[var(--qn-foreground-subtle)]">Source</span><p className="font-medium text-[var(--qn-foreground)]">{summary.policy?.optimization_source}</p></div>
              <div><span className="text-[var(--qn-foreground-subtle)]">Last Optimized</span><p className="font-medium text-[var(--qn-foreground)]">{summary.policy?.last_optimized ? formatTimeAgo(summary.policy.last_optimized) : "Never"}</p></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export default function KnowledgeBase() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("documents");

  const fetchDocuments = useCallback(async () => {
    try {
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/knowledge/documents`),
        axios.get(`${API}/knowledge/stats`),
      ]);
      setDocuments(docsRes.data.documents || []);
      setStats(statsRes.data);
    } catch (err) { console.error("Failed to fetch knowledge base:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/knowledge/documents/${docId}`);
      toast.success(t("knowledge.documents.delete", "Document deleted"));
      fetchDocuments();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-5" data-testid="knowledge-base-page" data-automation-id="knowledge-base-page">
      {/* Header Stats */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t("knowledge.analytics.documents", "Documents")} value={stats?.total_documents ?? 0} icon={FileText}
          subtitle={`${stats?.ready_documents ?? 0} ready`} />
        <StatCard label={t("knowledge.analytics.chunks", "Chunks")} value={stats?.total_chunks ?? 0} icon={Layers}
          subtitle={`${(stats?.total_tokens ?? 0).toLocaleString()} tokens`} />
        <StatCard label="Storage" value={formatBytes(stats?.total_size_bytes)} icon={Database}
          subtitle={stats?.storage_quota ? `${stats.storage_quota.used_mb} / ${stats.storage_quota.quota_mb} MB` : undefined} />
        <StatCard label="Retrievals" value={stats?.total_retrievals ?? 0} icon={Search} />
      </motion.div>

      {/* Tabs */}
      <motion.div variants={staggerItem}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[var(--qn-surface)] border border-[var(--qn-border)]">
            <TabsTrigger value="documents" data-testid="kb-tab-documents" data-automation-id="kb-tab-documents" className="text-xs gap-1.5">
              <FileText className="w-3.5 h-3.5" />{t("knowledge.tabs.documents", "Documents")}
            </TabsTrigger>
            <TabsTrigger value="upload" data-testid="kb-tab-upload" data-automation-id="kb-tab-upload" className="text-xs gap-1.5">
              <Upload className="w-3.5 h-3.5" />{t("knowledge.tabs.upload", "Upload")}
            </TabsTrigger>
            <TabsTrigger value="search" data-testid="kb-tab-search" data-automation-id="kb-tab-search" className="text-xs gap-1.5">
              <Search className="w-3.5 h-3.5" />{t("knowledge.tabs.search", "Search")}
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="kb-tab-chat" data-automation-id="kb-tab-chat" className="text-xs gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />Chat
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="kb-tab-analytics" data-automation-id="kb-tab-analytics" className="text-xs gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" />{t("knowledge.tabs.analytics", "Analytics")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="documents" className="mt-4">
            <DocumentsTab documents={documents} loading={loading} onDelete={handleDelete} onRefresh={fetchDocuments} />
          </TabsContent>
          <TabsContent value="upload" className="mt-4">
            <UploadTab onRefresh={fetchDocuments} />
          </TabsContent>
          <TabsContent value="search" className="mt-4">
            <SearchTab />
          </TabsContent>
          <TabsContent value="chat" className="mt-4">
            <ChatTab />
          </TabsContent>
          <TabsContent value="analytics" className="mt-4">
            <AnalyticsTab />
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.div>
  );
}
